var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import AdminPageLayout from '../../../layout/admin-page-layout';
import { useFetch } from '@rad/hooks';
import Input from '@rad/components/inputs/input';
import TypeAhead from '@rad/components/inputs/type-ahead';
import Modal from '@rad/components/ui/modal';
import createTable from '@rad/components/ui/table';
import ActionButton from '@rad/components/ui/action-button';
import { expand } from '@rad/utility/validation';
import { getPropertyTags, getIcons, createPropertyTag, updatePropertyTag, deletePropertyTag } from '../../../common/network/property-tag';
import { createPropertyTagState } from '../../../types';
import Icon from '../../_shared/components/icon';
import { useToast } from '../../../contexts/toast';
const PropertyTags = () => {
    var _a;
    const toast = useToast();
    const [tagsPromise, reloadTags] = useFetch(getPropertyTags());
    const [iconsPromise] = useFetch(getIcons());
    const [showModal, setShowModal] = React.useState(false);
    const [editingId, setEditingId] = React.useState(null);
    const [form, setForm] = React.useState(createPropertyTagState());
    const [errors, setErrors] = React.useState({});
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [deletingId, setDeletingId] = React.useState(null);
    const TagsTable = React.useMemo(() => createTable(tagsPromise), [tagsPromise]);
    const [selectedIcon, setSelectedIcon] = React.useState(undefined);
    const handleAdd = () => {
        setEditingId(null);
        setForm(createPropertyTagState());
        setSelectedIcon(undefined);
        setErrors({});
        setShowModal(true);
    };
    const handleEdit = (tag) => {
        setEditingId(tag.id);
        setForm(Object.assign(Object.assign({}, tag), { icon: tag.icon.name }));
        setSelectedIcon(tag.icon);
        setErrors({});
        setShowModal(true);
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        setErrors({});
        const [success, err] = editingId
            ? yield updatePropertyTag(editingId, form)
            : yield createPropertyTag(form);
        if (success) {
            setShowModal(false);
            reloadTags();
            toast.success(editingId ? 'Tag updated' : 'Tag created');
        }
        else {
            const [, expanded] = expand(err);
            setErrors(expanded);
            toast.error('Please fix the errors below');
        }
    });
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!deletingId)
            return;
        yield deletePropertyTag(deletingId);
        setShowDeleteModal(false);
        setDeletingId(null);
        reloadTags();
        toast.success('Tag deleted');
    });
    return (_jsx(AdminPageLayout, { children: _jsxs("div", { className: "container py-4", children: [_jsx("h5", { className: "fw-semibold mb-3", children: "Property Tags" }), _jsx("div", { className: "d-flex justify-content-between align-items-center mb-4", children: _jsxs("button", { className: "btn btn-primary", onClick: handleAdd, children: [_jsx("i", { className: "bi bi-plus-lg me-2" }), "Add Property Tag"] }) }), _jsxs(TagsTable, { className: "mb-0", children: [_jsx(TagsTable.Column, { header: "Icon", render: tag => _jsx(Icon, { name: tag.icon.name, color: tag.color, size: 1.5 }) }), _jsx(TagsTable.Column, { header: "Name", accessor: "display" }), _jsx(TagsTable.Column, { header: "Color", render: tag => (_jsxs("div", { className: "d-flex align-items-center gap-2", children: [_jsx("div", { style: {
                                            width: 24,
                                            height: 24,
                                            backgroundColor: tag.color,
                                            border: '1px solid #dee2e6',
                                            borderRadius: 4,
                                        } }), _jsx("span", { children: tag.color })] })) }), _jsx(TagsTable.Column, { header: "", className: "text-end", render: tag => (_jsx(_Fragment, { children: _jsx("button", { className: "btn btn-link text-primary p-0 me-2", onClick: () => handleEdit(tag), title: "Edit", children: _jsx("i", { className: "bi bi-pencil" }) }) })) })] }), _jsx(Modal, { isOpen: showModal, title: editingId ? 'Edit Tag' : 'Add Tag', onClose: () => setShowModal(false), onSave: handleSave, children: _jsxs("div", { className: "d-flex flex-column gap-3", children: [!form.id && _jsx(Input, { kind: "text", label: "Name", value: form.name, onChange: v => setForm(Object.assign(Object.assign({}, form), { name: v })), invalid: errors.name }), _jsx(Input, { kind: "text", label: "Display", value: form.display, onChange: v => setForm(Object.assign(Object.assign({}, form), { display: v })), invalid: errors.display }), _jsx(Input, { kind: "color", label: "Color", value: form.color, onChange: v => setForm(Object.assign(Object.assign({}, form), { color: v })), invalid: errors.color }), _jsx(TypeAhead, { label: "Icon", options: iconsPromise, value: selectedIcon, onChange: icon => {
                                    setSelectedIcon(icon);
                                    setForm(Object.assign(Object.assign({}, form), { icon: icon.name }));
                                }, optionKey: (i) => i.name, optionLabel: (i) => (_jsxs(_Fragment, { children: [_jsx(Icon, { name: i.name, size: 1.2, className: "me-2" }), i.display] })), inputLabel: (i) => i.display, renderSelectedValue: (i) => (_jsxs(_Fragment, { children: [_jsx(Icon, { name: i.name, size: 1.2, className: "me-2" }), i.display] })), invalid: (_a = errors.icon) === null || _a === void 0 ? void 0 : _a[0] }), errors.form && _jsx("div", { className: "alert alert-danger", children: errors.form.join(', ') })] }) }), _jsx(Modal, { isOpen: showDeleteModal, title: "Delete Tag", onClose: () => setShowDeleteModal(false), footer: _jsx(ActionButton, { className: "btn btn-danger", onClick: handleDeleteConfirm, loadingText: "Deleting...", children: "Delete" }), children: _jsx("p", { children: "Are you sure you want to delete this tag? This action cannot be undone." }) })] }) }));
};
export default PropertyTags;
